const SELECTOR = 'product-recommendations';
const RENDER_MARGIN = '1200px 0px';
let runtimePromise;
const states = new Map();
const initializedCarousels = new WeakSet();

const updateCarouselNavigation = (root) => {
  const track = root.querySelector('[data-kv-related-track]');
  if (!(track instanceof HTMLElement)) return;

  const previous = root.querySelector('[data-kv-related-prev]');
  const next = root.querySelector('[data-kv-related-next]');
  const max = Math.max(0, track.scrollWidth - track.clientWidth);

  if (previous instanceof HTMLButtonElement) previous.hidden = track.scrollLeft <= 2;
  if (next instanceof HTMLButtonElement) next.hidden = track.scrollLeft >= max - 2;
};

const carouselPageWidth = (root) => {
  const track = root.querySelector('[data-kv-related-track]');
  const card = track?.querySelector('[data-kv-related-card]');
  if (!(track instanceof HTMLElement) || !(card instanceof HTMLElement)) return 0;

  const styles = getComputedStyle(track);
  const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
  const columns = window.matchMedia('(max-width: 749px)').matches ? 2 : 4;
  return (card.getBoundingClientRect().width + gap) * columns;
};

const moveCarousel = (root, direction) => {
  const track = root.querySelector('[data-kv-related-track]');
  if (!(track instanceof HTMLElement)) return;

  const distance = carouselPageWidth(root);
  if (!distance) return;
  track.scrollBy({ left: distance * direction, behavior: 'smooth' });
};

const initializeCarouselRoot = (root) => {
  if (!(root instanceof HTMLElement)) return;

  const track = root.querySelector('[data-kv-related-track]');
  if (!(track instanceof HTMLElement)) return;

  if (!initializedCarousels.has(root)) {
    initializedCarousels.add(root);

    let updateFrame = 0;
    track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(updateFrame);
        updateFrame = requestAnimationFrame(() => updateCarouselNavigation(root));
      },
      { passive: true }
    );

    root.querySelector('[data-kv-related-prev]')?.addEventListener('click', () => moveCarousel(root, -1));
    root.querySelector('[data-kv-related-next]')?.addEventListener('click', () => moveCarousel(root, 1));
  }

  updateCarouselNavigation(root);
};

const initializeCarousel = (scope = document) => {
  if (!(scope instanceof Document || scope instanceof Element)) return;

  if (scope instanceof Element && scope.matches('[data-kv-related-products]')) {
    initializeCarouselRoot(scope);
  }

  scope.querySelectorAll?.('[data-kv-related-products]').forEach(initializeCarouselRoot);
};

const readManifest = () => {
  const node = document.getElementById('KervelanRelatedConfig');
  if (!node?.textContent) return { presentationCss: [] };
  try {
    return JSON.parse(node.textContent);
  } catch (error) {
    console.warn('[KERVELAN] Related Products manifest is invalid', error);
    return { presentationCss: [] };
  }
};

const manifest = readManifest();

const getState = (target) => {
  const key = target.dataset.sectionId || target.id;
  if (!states.has(key)) states.set(key, { key });
  return states.get(key);
};

const currentTarget = (state) =>
  document.querySelector(`${SELECTOR}[data-section-id="${CSS.escape(state.key)}"]`) ||
  document.getElementById(`product-recommendations-${state.key}`);

const loadStylesheet = (url) => {
  if (!url) return Promise.resolve();
  const absolute = new URL(url, document.baseURI).href;
  const existing = [...document.querySelectorAll('link[rel="stylesheet"]')].find((link) => link.href === absolute);
  if (existing) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;
    link.addEventListener('load', resolve, { once: true });
    link.addEventListener('error', reject, { once: true });
    document.head.append(link);
  });
};

const ensureRuntime = () => {
  if (!runtimePromise) {
    runtimePromise = import('@theme/product-recommendations-runtime').catch((error) => {
      runtimePromise = undefined;
      throw error;
    });
  }
  return runtimePromise.then(() => customElements.whenDefined('product-recommendations'));
};

const prefetch = (target, state = getState(target)) => {
  if (target.dataset.recommendationsPerformed === 'true') return Promise.resolve(true);
  if (!state.prefetchPromise) {
    state.prefetchPromise = ensureRuntime()
      .then(() => target.prefetchRecommendations?.())
      .catch((error) => {
        state.prefetchPromise = undefined;
        console.warn('[KERVELAN] Related Products prefetch failed', error);
        return false;
      });
  }
  return state.prefetchPromise;
};

const render = (target, state = getState(target)) => {
  if (target.dataset.recommendationsPerformed === 'true') {
    initializeCarousel(target);
    return Promise.resolve(true);
  }
  if (state.renderPromise) return state.renderPromise;

  state.renderPromise = Promise.all([
    prefetch(target, state),
    ...manifest.presentationCss.map(loadStylesheet),
  ])
    .then(() => target.renderRecommendations?.())
    .then((success) => {
      if (!success) return false;
      requestAnimationFrame(() => initializeCarousel(currentTarget(state)));
      return true;
    })
    .catch((error) => {
      state.renderPromise = undefined;
      console.warn('[KERVELAN] Related Products render failed', error);
      return false;
    });

  return state.renderPromise;
};

const scheduleIdlePrefetch = (target, state) => {
  if (navigator.connection?.saveData || /2g/.test(navigator.connection?.effectiveType || '')) return;

  const schedule = () => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => prefetch(target, state), { timeout: 2500 });
    } else {
      window.setTimeout(() => prefetch(target, state), 1200);
    }
  };

  if (document.readyState === 'complete') schedule();
  else window.addEventListener('load', schedule, { once: true });
};

const bind = (target) => {
  if (!(target instanceof HTMLElement) || target.dataset.kvCoordinatorBound === 'true') return;
  target.dataset.kvCoordinatorBound = 'true';
  initializeCarousel(target);
  const state = getState(target);

  if (window.Shopify?.designMode || !('IntersectionObserver' in window)) {
    render(target, state).catch(() => {});
    return;
  }

  scheduleIdlePrefetch(target, state);
  state.renderObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      render(target, state);
    },
    { rootMargin: RENDER_MARGIN, threshold: 0.01 }
  );
  state.renderObserver.observe(target);
};

const bindAll = (root = document) => {
  if (root instanceof Element && root.matches(SELECTOR)) bind(root);
  root.querySelectorAll?.(SELECTOR).forEach(bind);
};

bindAll();
document.addEventListener('shopify:section:load', (event) => bindAll(event.target));
window.addEventListener('resize', () => initializeCarousel());
